import React, { useState, useRef } from "react";
import Page from "src/components/Page";
import Logo from "../components/Logo";
import { LoginForm } from "../components/authentication/login";
import { RegisterForm } from "../components/authentication/register";
import ResetPasswordForm from "../components/authentication/ResetPasswordForm";
import PasswordResetConfirmForm from "src/components/authentication/PasswordResetConfirmForm";
import Alert from "../utils/Alert";
import { Box, Grid, Stack, Typography, Link } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useParams } from "react-router-dom";
import { useTranslation, Trans } from 'react-i18next';
import {useRegionConfig } from "src/RegionConfigProvider";


const LandingPage = () => {

    const { logo = {}, landing } = useRegionConfig();

    // const [authStep, setAuthStep] = useState("login");
    const { authStep, uid, token } = useParams();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const formRef = useRef();
    const { t } = useTranslation();

    const LINKS = [
        { step: "login", label: t("LandingPage.links.login") },
        { step: "register", label: t("LandingPage.links.register") },
        { step: "reset_password", label: t("LandingPage.links.reset_password") },
    ];

    const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
        );
    const mainColumnsSpacing = isMobile ? 6 : 3;

    const FORMS = [
        {
            step: "login",
            form: (
                <LoginForm
                    ref={formRef}
                    setSubmitting={(val) => {
                        setIsSubmitting(val);
                    }}
                />
            ),
            buttonLabel: t("LandingPage.buttonLabels.login"),
            links: [LINKS[2], LINKS[1]],
        },
        {
            step: "register",
            form: (
                <RegisterForm
                    ref={formRef}
                    setSubmitting={(val) => {
                        setIsSubmitting(val);
                    }}
                />
            ),
            buttonLabel: t("LandingPage.buttonLabels.register"),
            links: [LINKS[0]],
        },
        {
            step: "reset_password",
            form: (
                <ResetPasswordForm
                    ref={formRef}
                    setSubmitting={(val) => {
                        setIsSubmitting(val);
                    }}
                />
            ),
            buttonLabel: t("LandingPage.buttonLabels.reset_password"),
            links: [LINKS[0]],
        },
        {
            step: "reset_password_confirm",
            form: (
                <PasswordResetConfirmForm
                    uid={uid}
                    token={token}
                    ref={formRef}
                    setSubmitting={(val) => {
                        setIsSubmitting(val);
                    }}
                />
            ),
            buttonLabel: t("LandingPage.buttonLabels.reset_password_confirm"),
            links: [LINKS[0]],
        },
    ];

    const renderAuthPart = () => {
        let authForm = null;
        if (uid && token) {
            authForm = FORMS.find(
                (form) => form.step === "reset_password_confirm"
            );
        } else {
            authForm = FORMS.find((form) => form.step === authStep);
        }
        if (!authForm) {
            authForm = FORMS.find((form) => form.step === "login");
        }
        let elements = [];
        elements.push(authForm.form);
        elements.push(
            <LoadingButton
                variant="contained"
                onClick={() => {
                    formRef.current.submitForm();
                }}
                loading={isSubmitting}
            >
                {authForm.buttonLabel}
            </LoadingButton>
        );
        for (const link of authForm.links) {
            elements.push(
                <Link href={`/${link.step}`} underline="none" color="inherit">
                    {link.label}
                </Link>
            );
        }

        return elements.map((element, index) => ({
            ...element,
            key: { index },
        }));
    };

    const renderLandingItem = (item, key) => {
        if (!item || typeof item !== "object") {
            return null;
        }

        const type = item.type || (Array.isArray(item.items) ? "group" : "iconLink");

        if (type === "group") {
            return (
                <Stack
                    key={key}
                    direction={item.direction || "row"}
                    spacing={item.spacing ?? 1}
                    justifyContent={item.justifyContent || "center"}
                    alignItems={item.alignItems || "center"}
                    flexWrap={item.flexWrap || "nowrap"}
                    useFlexGap
                    sx={item.sx}
                >
                    {(item.items || []).map((nestedItem, index) =>
                        renderLandingItem(nestedItem, `${key}-${index}`)
                    )}
                </Stack>
            );
        }

        if (type === "text") {
            const textContent = item.textKey ? t(item.textKey) : item.text;
            if (!textContent) {
                return null;
            }
            const textElement = (
                <Typography variant={item.variant || "body1"} sx={item.sx}>
                    {textContent}
                </Typography>
            );

            if (item.url) {
                return (
                    <Box
                        key={key}
                        component="a"
                        href={item.url}
                        target={item.target || "_blank"}
                        rel={item.rel || "noopener noreferrer"}
                        sx={{
                            color: "inherit",
                            textDecoration: "none",
                            display: "inline-flex",
                            justifyContent: "center",
                        }}
                    >
                        {textElement}
                    </Box>
                );
            }

            return (
                <Box key={key}>
                    {textElement}
                </Box>
            );
        }

        const src = item.icon || item.src;
        if (!src) {
            return null;
        }

        const image = (
            <Box
                component="img"
                src={src}
                alt={item.alt || ""}
                sx={{
                    width: item.width ?? 50,
                    height: "auto",
                    display: "block",
                    ...item.imgSx,
                }}
            />
        );

        if (item.url) {
            return (
                <Box
                    key={key}
                    component="a"
                    href={item.url}
                    target={item.target || "_blank"}
                    rel={item.rel || "noopener noreferrer"}
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        ...item.sx,
                    }}
                >
                    {image}
                </Box>
            );
        }

        return (
            <Box
                key={key}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    ...item.sx,
                }}
            >
                {image}
            </Box>
        );
    };

    const renderLandingSections = () => {
        if (!Array.isArray(landing?.sections) || landing.sections.length === 0) {
            return null;
        }

        return (
            <Stack spacing={landing.spacing ?? 2} sx={landing.sx}>
                {landing.sections.map((section, sectionIndex) => (
                    <Stack
                        key={`section-${sectionIndex}`}
                        direction={section.direction || "row"}
                        spacing={section.spacing ?? 3}
                        justifyContent={section.justifyContent || "center"}
                        alignItems={section.alignItems || "center"}
                        flexWrap={section.flexWrap || "nowrap"}
                        useFlexGap
                        sx={section.sx}
                    >
                        {(section.items || []).map((item, itemIndex) =>
                            renderLandingItem(item, `section-${sectionIndex}-${itemIndex}`)
                        )}
                    </Stack>
                ))}
            </Stack>
        );
    };

    return (
        <Page title="">
            {/* {isWinter() && <Snowfall />} */}
            <Alert />

            <Stack
                justifyContent="center"
                sx={{
                    minHeight: "100vh",
                }}
                spacing={8}
            >
                <Grid
                    container
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                        textAlign: "center",
                        // minHeight: "100vh",
                    }}
                >
                    <Grid item xs={mainColumnsSpacing}>
                        <Stack
                            justifyContent="center"
                            alignItems="center"
                            spacing={2}
                        >
                            <Typography variant="h3">
                                <Trans i18nKey="LandingPage.welcome.title" components={{ 1: <br /> }} />
                            </Typography>
                            <Typography>
                                <Trans i18nKey="LandingPage.welcome.description" components={{ 1: <br /> }} />
                            </Typography>
                            <Logo url={logo.url}/>
                        </Stack>
                    </Grid>
                    <Grid item xs={mainColumnsSpacing}>
                        <Stack
                            justifyContent="center"
                            alignItems="center"
                            spacing={1}
                        >
                            <Typography variant="h5">
                                <Trans i18nKey="LandingPage.form.title" components={{ 1: <br /> }} />
                            </Typography>
                            {renderAuthPart()}
                        </Stack>
                    </Grid>
                </Grid>
                {renderLandingSections()}
            </Stack>
        </Page>
    );
};

export default LandingPage;
